/**
 * Populates a subfolder of a project's vfs-service project from an external
 * source (migration/vfs-adk-generation) — used by CreateServiceModal.jsx's
 * "Create new service" flow so a new service no longer means provisioning a
 * brand-new GitLab repository (the old services/create / createServiceFromGit
 * behavior).
 *
 * Two distinct source shapes, two distinct vfs-service operations — do not
 * conflate them:
 *
 * - `cloneGitRepoIntoVfsFolder` — a real, clonable git repository (the
 *   "Import from Git" tab). Runs server-side as a real `git clone` (POST
 *   /clone, see vfs-service's src/history/externalClone.js).
 * - `importArchiveIntoVfsFolder` — a downloadable release archive
 *   (.tar.gz/.tgz/.zip), NOT a git repo (the "Create from Template" tab: a
 *   Console marketplace template's source turns out to be published this
 *   way — resources.services.*.archiveUrl, discovered empirically via
 *   mia-flow-bff's GET /api/mia-platform/services/template-source, which
 *   also classifies which of the two a given URL is). Runs server-side as a
 *   download + extract (POST /import-archive, see vfs-service's
 *   src/history/externalArchive.js).
 *
 * Both run SERVER-SIDE, in vfs-service, not client-side through a git
 * provider's REST API. That's a deliberate choice, not just an
 * implementation detail: a provider REST API needs an authenticated, linked
 * account even to read a PUBLIC repository through this app's own OAuth-
 * broker flow, and requiring that up front is exactly the git-connection
 * dependency this migration removes. Neither a plain `git clone` nor a plain
 * archive download ever needed that for a public source.
 */

import config from '../config';
import { getAuthorizedFetch } from './fetchRegistry';
import { buildAclContextHeaders } from './aclContext';
import * as vfsClient from './vfsClient';

function vfsHeaders() {
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...buildAclContextHeaders(),
  };
}

async function postToVfs(vfsProjectId, path, body, describeError) {
  if (!config.vfsServiceUrl) {
    throw new Error('vfs-service is not configured (vfsServiceUrl is unset)');
  }
  const response = await getAuthorizedFetch()(
    `${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/projects/${encodeURIComponent(vfsProjectId)}${path}`,
    { method: 'POST', headers: vfsHeaders(), body: JSON.stringify(body) }
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`${describeError} (${response.status}): ${detail}`);
  }
  const result = await response.json();
  vfsClient.markProjectVfsBacked(vfsProjectId);
  return result;
}

/**
 * @param {string} vfsProjectId - vfs-service project id (the Console project id).
 * @param {string} subfolder - where to clone into, relative to the project root (e.g. a service name).
 * @param {string} repoUrl - the repository to clone. Public repos only for now (see externalClone.js).
 * @returns {Promise<{subfolder: string, sha: string}>}
 */
export async function cloneGitRepoIntoVfsFolder(vfsProjectId, subfolder, repoUrl) {
  return postToVfs(vfsProjectId, '/clone', { repoUrl, subfolder }, `vfs-service clone failed for ${repoUrl} -> ${subfolder}`);
}

/** A clone vfs-service refused: its words, status, `code` (e.g. `no_account`, `account_refused`) and `connectUrl`. */
export class CloneError extends Error {
  constructor(message, status, code = null, connectUrl = null) {
    super(message);
    this.name = 'CloneError';
    this.status = status;
    this.code = code;
    this.connectUrl = connectUrl;
  }
}

/**
 * "Start from git": clones `repoUrl` into `services/<name>` of a project with the person's own account for its host
 * when it is private (`account`: an account id; none: their default for that host, else anonymous), keeping `origin`
 * (keepRemote) so pull and push work later. The URL never goes into an error message or a log: vfs-service's own
 * message (tokens scrubbed) is what is shown. Resolves to `{ subfolder, sha, usedAccount, repoUrl, convertedFrom? }`.
 */
export async function cloneRepoIntoProject(vfsProjectId, { repoUrl, subfolder, account = null }) {
  if (!config.vfsServiceUrl) { throw new CloneError('vfs-service is not configured.', 0, 'vfs_off'); }
  let response;
  try {
    response = await getAuthorizedFetch()(
      `${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/projects/${encodeURIComponent(vfsProjectId)}/clone`,
      { method: 'POST', headers: vfsHeaders(), body: JSON.stringify({ repoUrl, subfolder, keepRemote: true, ...(account ? { account } : {}) }) }
    );
  } catch (error) {
    throw new CloneError(`Your sandbox could not be reached (${error?.message || 'network error'}).`, 0, 'unreachable');
  }
  let body = null;
  try { body = await response.json(); } catch { body = null; }
  if (!response.ok) {
    const fallback = response.status === 409 ? `${subfolder} already exists: pick another name.` : `The clone failed (${response.status}).`;
    throw new CloneError(body?.error || body?.message || fallback, response.status, body?.code || null, body?.connectUrl || null);
  }
  vfsClient.markProjectVfsBacked(vfsProjectId);
  return body || { subfolder };
}

/**
 * @param {string} vfsProjectId - vfs-service project id (the Console project id).
 * @param {string} subfolder - where to extract into, relative to the project root (e.g. a service name).
 * @param {string} archiveUrl - a downloadable .tar.gz/.tgz/.zip URL. Public URLs only (see externalArchive.js).
 * @returns {Promise<{subfolder: string, fileCount: number, sha: string}>}
 */
export async function importArchiveIntoVfsFolder(vfsProjectId, subfolder, archiveUrl) {
  return postToVfs(vfsProjectId, '/import-archive', { archiveUrl, subfolder }, `vfs-service archive import failed for ${archiveUrl} -> ${subfolder}`);
}

export default { cloneGitRepoIntoVfsFolder, cloneRepoIntoProject, importArchiveIntoVfsFolder };
